import React, { useState, useEffect, useCallback } from 'react';
import { getPlayers, getClasses, verifyToken } from './api';
import Navbar from './components/Navbar';
import ClassFilter from './components/ClassFilter';
import PlayerGrid from './components/PlayerGrid';
import AdminLogin from './components/Admin/AdminLogin';
import AdminDashboard from './components/Admin/AdminDashboard';
import CookieBanner from './components/CookieBanner';

const App = () => {
  const [players, setPlayers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [adminView, setAdminView] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Verify token on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyToken().then(() => setIsLoggedIn(true)).catch(() => {
        localStorage.removeItem('adminToken');
        setIsLoggedIn(false);
      }).finally(() => setCheckingAuth(false));
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedClass) params.class_year = selectedClass;
      if (searchTerm) params.search = searchTerm;
      const data = await getPlayers(params);
      setPlayers(data);
    } catch (err) {
      console.error('Failed to fetch players:', err);
    }
    setLoading(false);
  }, [selectedClass, searchTerm]);

  const fetchClasses = useCallback(async () => {
    try {
      const data = await getClasses();
      setClasses(data);
      if (data.length > 0 && !selectedClass) {
        setSelectedClass(data[data.length - 1]); // default to latest class
      }
    } catch (err) {
      console.error('Failed to fetch classes:', err);
    }
  }, [selectedClass]);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const handleAdminClick = async () => {
    if (isLoggedIn) {
      // Re-verify token before showing admin
      try {
        await verifyToken();
        setAdminView(true);
      } catch {
        localStorage.removeItem('adminToken');
        setIsLoggedIn(false);
        setAdminView(true); // show login form
      }
    } else {
      setAdminView(!adminView);
    }
  };

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    setAdminView(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setAdminView(false);
  };

  return (
    <div className="app">
      <CookieBanner />
      <Navbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAdminClick={handleAdminClick}
        checkingAuth={checkingAuth}
      />

      {adminView ? (
        isLoggedIn ? (
          <AdminDashboard onLogout={handleLogout} refreshPlayers={fetchPlayers} />
        ) : (
          <AdminLogin onLoginSuccess={handleLoginSuccess} />
        )
      ) : (
        <main className="main-content">
          <div className="hero-section">
            <h1>NBA Draft Prospect Board</h1>
            <p>Tracking the top basketball prospects across all classes</p>
          </div>

          <ClassFilter
            classes={classes}
            selectedClass={selectedClass}
            onSelectClass={setSelectedClass}
          />

          {loading ? (
            <div className="loading">Loading prospects...</div>
          ) : (
            <PlayerGrid players={players} refreshPlayers={fetchPlayers} />
          )}
        </main>
      )}

      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: #0f0f1a;
          color: #e0e0e0;
          min-height: 100vh;
        }
        .app { min-height: 100vh; }
        .main-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
        }
        .hero-section {
          text-align: center;
          padding: 40px 20px 20px;
        }
        .hero-section h1 {
          font-size: 2.4rem;
          font-weight: 800;
          background: linear-gradient(135deg, #f7971e, #ffd200);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 8px;
        }
        .hero-section p {
          color: #8888aa;
          font-size: 1.1rem;
        }
        .loading {
          text-align: center;
          padding: 60px;
          color: #8888aa;
          font-size: 1.1rem;
        }
      `}</style>
    </div>
  );
};

export default App;
// v3
